import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Alert, Button } from '@rallyshift/design-system'
import { Megaphone, MessageSquare, PenSquare } from 'lucide-react'
import api, { type Conversation } from '../api/client'
import { useAuth } from '../contexts/AuthContext'
import { useLocale } from '../contexts/LocaleContext'
import { useOpenConversation } from '../hooks/useOpenConversation'
import { useRealtime } from '../hooks/useRealtime'
import ConversationList from '../components/messages/ConversationList'
import ConversationThread from '../components/messages/ConversationThread'
import NewConversationModal from '../components/messages/NewConversationModal'

/**
 * List and thread side by side on a wide screen; on a phone the route decides which
 * one is shown, so the back button behaves as expected.
 */
const MessagesPage: React.FC = () => {
  const { conversationId } = useParams<{ conversationId?: string }>()
  const { t } = useLocale()
  const [conversations, setConversations] = useState<Conversation[]>([])
  const [active, setActive] = useState<Conversation | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [picking, setPicking] = useState(false)
  const pending = useRef<ReturnType<typeof setTimeout> | null>(null)
  const { profile, isAdmin } = useAuth()
  const isOrganiser = isAdmin || profile?.role === 'coordinator'
  const { open: openChat, opening, error: chatError } = useOpenConversation()

  const loadList = useCallback(async () => {
    try {
      setConversations(await api.messaging.list())
      setError(null)
    } catch (err: any) {
      setError(err?.message || '')
    } finally {
      setLoading(false)
    }
  }, [])

  const refreshSoon = useCallback(() => {
    if (pending.current) clearTimeout(pending.current)
    pending.current = setTimeout(loadList, 250)
  }, [loadList])

  useEffect(() => {
    loadList()
    return () => {
      if (pending.current) clearTimeout(pending.current)
    }
  }, [loadList])

  // An empty channel is not in the list yet, so a link straight to one loads it alone.
  useEffect(() => {
    if (!conversationId) {
      setActive(null)
      return
    }
    const known = conversations.find((c) => c.id === conversationId)
    if (known) {
      setActive((current) => (current?.id === known.id ? { ...current, ...known } : known))
      return
    }
    if (loading) return
    if (active?.id === conversationId) return
    let cancelled = false
    api.messaging
      .get(conversationId)
      .then((c) => !cancelled && setActive(c))
      .catch((err: any) => !cancelled && setError(err?.message || ''))
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `active` is only read to skip a refetch
  }, [conversationId, conversations, loading])

  useRealtime(() => refreshSoon())

  return (
    <div className="flex h-[calc(100dvh-7rem)] min-h-[420px] flex-col lg:h-[calc(100vh-3rem)]">
      <div className="mb-4 flex items-center justify-between gap-4">
        <div className={conversationId ? 'hidden lg:block' : ''}>
          <h1 className="font-display text-display-lg text-text-primary">{t('messages.title')}</h1>
          <p className="mt-1 text-md text-text-secondary">{t('messages.subtitle')}</p>
        </div>
        <div className={`flex flex-wrap justify-end gap-2 ${conversationId ? 'hidden lg:flex' : ''}`}>
          {isOrganiser && (
            <Button
              variant="secondary"
              icon={<Megaphone className="h-4 w-4" />}
              disabled={opening}
              onClick={() => openChat(() => api.messaging.forOrganization())}
            >
              {t('messages.announcements')}
            </Button>
          )}
          <Button variant="tenant" icon={<PenSquare className="h-4 w-4" />} onClick={() => setPicking(true)}>
            {t('messages.new')}
          </Button>
        </div>
      </div>

      {(error !== null || chatError) && (
        <div className="mb-3">
          <Alert variant="danger">{error || chatError || t('messages.error')}</Alert>
        </div>
      )}

      <div className="flex min-h-0 flex-1 overflow-hidden rounded-lg border border-border bg-surface-card shadow-card">
        <aside
          className={`min-h-0 w-full overflow-y-auto border-r border-border lg:block lg:w-80 lg:shrink-0 ${
            conversationId ? 'hidden' : 'block'
          }`}
        >
          {loading ? (
            <p className="p-4 text-sm text-text-muted">{t('common.loading')}</p>
          ) : conversations.length === 0 ? (
            <div className="p-6 text-center">
              <MessageSquare className="mx-auto h-10 w-10 text-text-muted" />
              <p className="mt-2 text-md font-semibold text-text-primary">{t('messages.empty')}</p>
              <p className="mt-1 text-sm text-text-secondary">{t('messages.emptyHint')}</p>
            </div>
          ) : (
            <ConversationList conversations={conversations} activeId={conversationId} />
          )}
        </aside>

        <section className={`min-h-0 min-w-0 flex-1 ${conversationId ? 'block' : 'hidden lg:block'}`}>
          {active ? (
            <ConversationThread key={active.id} conversation={active} onChanged={refreshSoon} />
          ) : (
            <div className="flex h-full items-center justify-center p-6 text-sm text-text-muted">
              {conversationId ? t('common.loading') : t('messages.selectConversation')}
            </div>
          )}
        </section>
      </div>

      <NewConversationModal open={picking} onClose={() => setPicking(false)} />
    </div>
  )
}

export default MessagesPage
